import { module } from "angular";
import { map } from "rxjs/operators";

import type { Observable } from "rxjs";

import { isNil } from "lodash";
import type { IResponse, ISearchResult, ISearchResultByType } from "App/Modules/Models";
import { EquipmentClassFunctions, SearchResultType } from "App/Modules/Models";
import { EquipmentClass } from "App/Modules/Models/Models/EquipmentClass";
import type { ISearchExtraProperty } from "App/Modules/Models/Models/ISearchExtraProperty";
import type { IHttpService } from "App/Modules/Services";
import type { ISearchRepository } from "./ISearchRepository";

export default class SearchRepository implements ISearchRepository {
    public static $inject = ["mmHttpService", "$translate"];

    public static enhanceSearchResult(
        searchResult: ISearchResult,
        type: SearchResultType,
        $translate: ng.translate.ITranslateService,
    ): void {
        searchResult.tags = SearchRepository.getTags(searchResult.extraProperties);
        searchResult.hits = SearchRepository.getHits(searchResult.extraProperties);

        switch (type) {
            case SearchResultType.Site:
                searchResult.icon = SearchRepository.getSiteIcon(searchResult.extraProperties);
                searchResult.attributes = SearchRepository.getSiteSubCaption(searchResult.extraProperties, $translate);
                searchResult.primaryHref = `#!/sites/${SearchRepository.getExtraPropertyValue(
                    searchResult.extraProperties,
                    "friendlyId",
                )}`;
                break;
            case SearchResultType.Team:
                searchResult.icon = "users";
                searchResult.attributes = SearchRepository.getTeamSubCaption(searchResult.extraProperties, $translate);
                searchResult.primaryHref = `#!/teams/${searchResult.id}`;
                break;
            case SearchResultType.Person:
                searchResult.icon = "user";
                searchResult.primaryHref = `#!/partners/${searchResult.partnerId}/users/${searchResult.id}`;
                searchResult.attributes = SearchRepository.getPersonSubCaption(
                    searchResult.extraProperties,
                    $translate,
                );
                searchResult.tags = SearchRepository.getTags(searchResult.extraProperties);
                break;
            case SearchResultType.Company:
                searchResult.icon = "briefcase";
                searchResult.attributes = SearchRepository.getCompanySubCaption(searchResult.extraProperties);
                searchResult.primaryHref = `#!/companies/${searchResult.id}`;
                break;
            case SearchResultType.Equipment:
                searchResult.icon = SearchRepository.getEquipmentIcon(searchResult.extraProperties);
                searchResult.attributes = SearchRepository.getEquipmentSubCaption(searchResult.extraProperties);
                break;
            case SearchResultType.Something:
                searchResult.icon = "star";
                searchResult.attributes = SearchRepository.getSomethingSubCaption(
                    searchResult.extraProperties,
                    $translate,
                );
                searchResult.primaryHref = "";
                break;
            default:
                throw Error("Unsupported search result type");
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private static getSiteIcon(extraProperties: ISearchExtraProperty[]) {
        return "warehouse-alt";
    }

    private static getEquipmentIcon(extraProperties: ISearchExtraProperty[]) {
        const eqTypeClass = SearchRepository.getExtraPropertyValue(extraProperties, "equipmentTypeClass");

        return EquipmentClassFunctions.EquipmentClassIcon(EquipmentClass[eqTypeClass as keyof typeof EquipmentClass]);
    }

    private static getHits(extraProperties: ISearchExtraProperty[]) {
        return extraProperties
            .filter((ep) => ep.isHit && ep.name !== "labels")
            .map(({ value }) => value)
            .join(", ");
    }

    private static getExtraPropertyValue(properties: ISearchExtraProperty[], name: string) {
        const property = properties.find((ep) => ep.name === name);

        return isNil(property) ? null : property.value;
    }

    private static getSiteSubCaption(
        extraProperties: ISearchExtraProperty[],
        $translate: ng.translate.ITranslateService,
    ) {
        const company = SearchRepository.getExtraPropertyValue(extraProperties, "companyName");
        const status = SearchRepository.getExtraPropertyValue(extraProperties, "statusName");

        return [company, $translate.instant(`Enums.SiteState.Enum${status}`)].filter((prop) => prop).join(", ");
    }

    private static getTeamSubCaption(
        extraProperties: ISearchExtraProperty[],
        $translate: ng.translate.ITranslateService,
    ) {
        const sitesCount = SearchRepository.getExtraPropertyValue(extraProperties, "siteCount");

        return $translate.instant("Entities.Team.NumSitesConnectedTo", { sitesCount }, "messageformat");
    }

    private static getPersonSubCaption(
        extraProperties: ISearchExtraProperty[],
        $translate: ng.translate.ITranslateService,
    ) {
        const rowRole = $translate.instant(SearchRepository.getExtraPropertyValue(extraProperties, "role"));
        const role = rowRole ? $translate.instant(`Entities.Role.${rowRole}`) : undefined;

        return $translate.instant("Entities.Role.RoleFormat", { role }, "messageformat");
    }

    private static getCompanySubCaption(extraProperties: ISearchExtraProperty[]) {
        return SearchRepository.getExtraPropertyValue(extraProperties, "partnerName");
    }

    private static getEquipmentSubCaption(extraProperties: ISearchExtraProperty[]) {
        const serialNumber = SearchRepository.getExtraPropertyValue(extraProperties, "serialNumber");
        const originalSerialNumber = SearchRepository.getExtraPropertyValue(extraProperties, "originalSerialNumber");

        return [serialNumber, originalSerialNumber].filter((prop) => prop).join(", ");
    }

    private static getSomethingSubCaption(
        extraProperties: ISearchExtraProperty[],
        $translate: ng.translate.ITranslateService,
    ) {
        const type = SearchRepository.getExtraPropertyValue(extraProperties, "searchEntityType");

        return $translate.instant(`Entities.${type}.${type}`);
    }

    private static getTags(extraProperties: ISearchExtraProperty[]) {
        return extraProperties.filter((ep) => ep.isHit && ep.name === "labels").map(({ value }) => value);
    }

    constructor(
        private readonly httpService: IHttpService,
        private readonly $translate: ng.translate.ITranslateService,
    ) {}

    public search(
        searchString: string,
        type: SearchResultType,
        count: number,
    ): Observable<IResponse<ISearchResultByType[]>> {
        const params = {
            numberOfEntities: count,
            searchThroughEntityTypes: SearchResultType[type],
            searchValue: searchString,
        };

        return this.httpService.get<ISearchResultByType[]>("search/byEntityTypes", params).pipe(
            map((response: IResponse<ISearchResultByType[]>) => {
                response.data.forEach((result: ISearchResultByType) => {
                    result.type = (SearchResultType as any)[result.type] as SearchResultType;

                    result.results.forEach((sr: ISearchResult) => {
                        SearchRepository.enhanceSearchResult(sr, result.type, this.$translate);
                    });
                });

                return response;
            }),
        );
    }
}

module("mmRepositories").service("mmSearchRepository", SearchRepository);
